/**
 * A chart's content as Chart.js draws it.
 *
 * Kept apart from the component that owns the canvas so what a chart looks
 * like can be read, and tested, without one: the colours, the axes, the
 * legend, and two small drawings Chart.js has no option for, the figures
 * printed on the chart and the words in a doughnut's hole.
 *
 * Both are drawn on the canvas itself rather than laid over it in HTML: the
 * export takes a picture of the canvas (see `registry.ts`), and anything not
 * on it would not be in the picture.
 */

import type {
  ArcElement,
  BarElement,
  Chart,
  ChartConfiguration,
  ChartDataset,
  LegendItem,
  Plugin,
  PointElement,
} from 'chart.js'

import {
  chartPalette,
  coloursByLabel,
  inkOn,
  labelColour,
  printedFigure,
  seriesColour,
  type ChartContent,
} from '@/domain/chart'

/** What a chart borrows from the document it is in. */
export interface ChartLook {
  /** The document's accent, which the palette is built from. */
  readonly accent: string
  /** The charter's colours, taken after the accent where the document asks for them. */
  readonly house?: readonly string[]
  /** The document's typeface, as a CSS font stack. */
  readonly font: string
}

/** The paper's ink, which figures outside a bar are printed in. */
const INK = '#16202c'
const GRID = 'rgba(0, 0, 0, 0.06)'
const TYPE_PX = 11
/** A label longer than this, in characters, is set on several lines under its bar. */
const LINE_CHARS = 16
/** A slice smaller than this share of the whole is too thin to print its figure in. */
const LEAST_SLICE = 0.04
/** A figure over a narrow bar is set smaller, down to this, before it would spill onto the next. */
const LEAST_TYPE_PX = 8
/** The share of its slot a bar takes, and of its category the bars take: room between them. */
const BAR_SHARE = 0.82
const CATEGORY_SHARE = 0.72

/** A figure printed on the chart, where it stands: so the next can stand clear of it. */
interface Printed {
  readonly x0: number
  readonly x1: number
  readonly top: number
  readonly bottom: number
}

function isRound(chart: ChartContent): boolean {
  return chart.chartType === 'pie' || chart.chartType === 'doughnut'
}

function isHorizontal(chart: ChartContent): boolean {
  return chart.chartType === 'bar' && chart.orientation === 'horizontal'
}

function isStacked(chart: ChartContent): boolean {
  return chart.chartType === 'bar' && chart.stacked
}

/** Words filled into lines no wider than `width`, each line as full as it will go. */
function filled(words: readonly string[], width: number): string[] {
  const lines: string[] = []
  let line = ''
  for (const word of words) {
    if (line && line.length + 1 + word.length > width) {
      lines.push(line)
      line = word
    } else {
      line = line ? `${line} ${word}` : word
    }
  }
  if (line) lines.push(line)
  return lines
}

/**
 * A long label broken at its spaces, so it stands under its bar instead of
 * turning on its side. Broken evenly: as narrow as it can be set without one
 * more line, so no word is left alone on the last.
 */
export function wrapped(label: string, width = LINE_CHARS): string | string[] {
  if (label.length <= width) return label
  const words = label.split(/\s+/).filter(Boolean)
  const lines = filled(words, width)
  if (lines.length < 2) return label
  for (let narrower = Math.max(...words.map((word) => word.length)); narrower < width; narrower++) {
    const even = filled(words, narrower)
    if (even.length <= lines.length) return even
  }
  return lines
}

/** The colour of one bar, point or slice: its label's, or its series'. */
function colourOf(
  chart: ChartContent,
  palette: readonly string[],
  series: number,
  at: number,
): string {
  return coloursByLabel(chart)
    ? labelColour(chart, at, palette)
    : seriesColour(chart, series, palette)
}

function datasetsOf(chart: ChartContent, palette: readonly string[]): ChartDataset[] {
  const round = isRound(chart)
  const byLabel = coloursByLabel(chart)
  const labelColours = chart.labels.map((_, at) => labelColour(chart, at, palette))
  return chart.series.map((series, at) => {
    const own = seriesColour(chart, at, palette)
    const fill = byLabel ? labelColours : own
    return {
      label: series.label,
      data: [...series.values],
      backgroundColor: fill,
      // A slice is parted from the next by a hairline of paper; a line is its own colour.
      borderColor: round ? '#ffffff' : chart.chartType === 'line' ? own : fill,
      borderWidth: round ? 1.5 : chart.chartType === 'line' ? 2 : 0,
      fill: false,
      tension: 0.25,
      pointRadius: 2.5,
      pointBackgroundColor: fill,
      // Bars set apart, as printed reports set them: room between a category's
      // bars and more between categories, so the figures over them never touch.
      barPercentage: BAR_SHARE,
      categoryPercentage: CATEGORY_SHARE,
    }
  })
}

/** The legend's entries when each bar wears its own colour: one per label, not one per series. */
function labelEntries(chart: ChartContent, palette: readonly string[]): LegendItem[] {
  return chart.labels.map((text, at) => {
    const colour = labelColour(chart, at, palette)
    return { text, fillStyle: colour, strokeStyle: colour, lineWidth: 0, hidden: false, index: at }
  })
}

/** The room the printed figures need outside the bars, so none is cut off at the edge. */
function padding(chart: ChartContent): { top: number; right: number } {
  if (!chart.showValues || isRound(chart) || isStacked(chart)) return { top: 0, right: 0 }
  return isHorizontal(chart) ? { top: 0, right: 48 } : { top: 18, right: 0 }
}

/** The figure, printed where it reads best: past a bar's end, in a slice or a stacked segment, over a point. */
function figuresPlugin(chart: ChartContent, look: ChartLook, palette: readonly string[]): Plugin {
  return {
    id: 'dmFigures',
    afterDatasetsDraw(drawn: Chart) {
      if (!chart.showValues) return
      const ctx = drawn.ctx
      ctx.save()
      ctx.font = `600 ${TYPE_PX}px ${look.font}`
      const printed: Printed[] = []
      drawn.data.datasets.forEach((dataset, series) => {
        if (!drawn.isDatasetVisible(series)) return
        const values = dataset.data.map((value) => (typeof value === 'number' ? value : Number.NaN))
        const total = values.reduce(
          (sum, value) => sum + (Number.isFinite(value) ? Math.abs(value) : 0),
          0,
        )
        drawn.getDatasetMeta(series).data.forEach((element, at) => {
          const value = values[at]
          if (value === undefined || !Number.isFinite(value)) return
          const text = printedFigure(value, chart.unit)
          const colour = colourOf(chart, palette, series, at)
          if (isRound(chart)) {
            if (total === 0 || Math.abs(value) / total < LEAST_SLICE) return
            const { x, y } = (element as ArcElement).tooltipPosition(true)
            draw(ctx, text, x ?? 0, y ?? 0, 'center', 'middle', inkOn(colour))
          } else if (chart.chartType === 'line') {
            const { x, y } = (element as PointElement).getProps(['x', 'y'], true)
            draw(ctx, text, x ?? 0, (y ?? 0) - 6, 'center', 'bottom', INK)
          } else {
            figureOnBar(
              ctx,
              chart,
              element as BarElement,
              { text, value, colour, font: look.font },
              printed,
            )
          }
        })
      })
      ctx.restore()
    },
  }
}

/** One figure to print on a bar: what it reads, the bar's colour, and the typeface it is set in. */
interface Figure {
  readonly text: string
  readonly value: number
  readonly colour: string
  readonly font: string
}

function figureOnBar(
  ctx: CanvasRenderingContext2D,
  chart: ChartContent,
  bar: BarElement,
  figure: Figure,
  printed: Printed[],
): void {
  const { text, value, colour } = figure
  const props = bar.getProps(['x', 'y', 'base', 'width'], true)
  const [x, y, base] = [props.x ?? 0, props.y ?? 0, props.base ?? 0]
  const horizontal = isHorizontal(chart)
  if (isStacked(chart)) {
    // Inside its segment, and only when it fits there.
    const room = horizontal ? Math.abs(x - base) : Math.abs(base - y)
    const needed = horizontal ? ctx.measureText(text).width + 6 : TYPE_PX + 4
    if (room < needed) return
    const [cx, cy] = horizontal ? [(x + base) / 2, y] : [x, (y + base) / 2]
    draw(ctx, text, cx, cy, 'center', 'middle', inkOn(colour))
  } else if (horizontal) {
    const ahead = value >= 0
    draw(ctx, text, x + (ahead ? 4 : -4), y, ahead ? 'left' : 'right', 'middle', INK)
  } else {
    standingFigure(ctx, figure, { x, y, slot: (props.width ?? 0) / BAR_SHARE }, printed)
  }
}

/**
 * A figure over a standing bar, clear of the figures already printed.
 *
 * Set smaller when it is wider than the bar's slot, so it does not spill over
 * its neighbour's; and if it would still run into one (two bars of much the
 * same height, side by side), raised by a line, as a designer staggers them.
 */
function standingFigure(
  ctx: CanvasRenderingContext2D,
  figure: Figure,
  at: { readonly x: number; readonly y: number; readonly slot: number },
  printed: Printed[],
): void {
  const up = figure.value >= 0
  let size = TYPE_PX
  ctx.font = `600 ${size}px ${figure.font}`
  while (size > LEAST_TYPE_PX && ctx.measureText(figure.text).width > at.slot - 2) {
    size -= 0.5
    ctx.font = `600 ${size}px ${figure.font}`
  }
  const half = ctx.measureText(figure.text).width / 2
  let foot = at.y + (up ? -4 : 4 + size)
  const clashes = (spot: number): boolean =>
    printed.some(
      (other) =>
        at.x - half < other.x1 &&
        other.x0 < at.x + half &&
        spot - size < other.bottom &&
        other.top < spot,
    )
  for (let tries = 0; tries < 2 && clashes(foot) && foot - size * 2 > 0; tries += 1)
    foot -= size + 1
  printed.push({ x0: at.x - half, x1: at.x + half, top: foot - size, bottom: foot })
  draw(ctx, figure.text, at.x, foot, 'center', 'bottom', INK)
  ctx.font = `600 ${TYPE_PX}px ${figure.font}`
}

function draw(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  align: CanvasTextAlign,
  baseline: CanvasTextBaseline,
  colour: string,
): void {
  ctx.textAlign = align
  ctx.textBaseline = baseline
  ctx.fillStyle = colour
  ctx.fillText(text, x, y)
}

/** The words in a doughnut's hole, as large as the hole allows. */
function centrePlugin(chart: ChartContent, look: ChartLook): Plugin {
  return {
    id: 'dmCentre',
    afterDatasetsDraw(drawn: Chart) {
      const text = chart.centre.trim()
      if (!text || chart.chartType !== 'doughnut') return
      const arc = drawn.getDatasetMeta(0).data[0] as ArcElement | undefined
      if (!arc) return
      const props = arc.getProps(['x', 'y', 'innerRadius'], true)
      const [x, y, innerRadius] = [props.x ?? 0, props.y ?? 0, props.innerRadius ?? 0]
      const ctx = drawn.ctx
      ctx.save()
      let size = Math.min(28, innerRadius * 0.42)
      ctx.font = `700 ${size}px ${look.font}`
      const room = innerRadius * 1.6
      const width = ctx.measureText(text).width
      if (width > room) {
        size = Math.max(8, (size * room) / width)
        ctx.font = `700 ${size}px ${look.font}`
      }
      draw(ctx, text, x, y, 'center', 'middle', INK)
      ctx.restore()
    },
  }
}

/** Everything Chart.js needs to draw a chart's content in a document's look. */
export function chartConfiguration(chart: ChartContent, look: ChartLook): ChartConfiguration {
  const palette = chartPalette(look.accent, look.house)
  const round = isRound(chart)
  const horizontal = isHorizontal(chart)
  const stacked = isStacked(chart)
  const byLabel = coloursByLabel(chart) && !round
  // The document's own typeface, so a chart does not arrive set in something
  // else on a page whose typography was deliberately chosen. Given to each
  // part that sets text: Chart.js takes a typeface for the whole chart only
  // as a global default, and its own is Helvetica, or whatever stands in for it.
  const font = { family: look.font, size: TYPE_PX }

  const category = {
    stacked,
    grid: { display: false },
    // Every label, standing upright: one left out or turned on its side is a
    // bar nobody can name. A long one is set on several lines instead.
    ticks: { color: INK, font, autoSkip: false, maxRotation: 0 },
  }
  const value = {
    display: chart.showAxis,
    stacked,
    beginAtZero: true,
    grid: { color: GRID },
    ticks: {
      font,
      callback: (tick: string | number) => printedFigure(Number(tick), chart.unit),
    },
  }

  return {
    type: chart.chartType,
    data: {
      labels: chart.labels.map((label) => (horizontal || round ? label : wrapped(label))),
      datasets: datasetsOf(chart, palette),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      // Drawn at twice the screen's resolution at least: the export takes a
      // picture of the canvas, and a print is read closer than a screen.
      devicePixelRatio: Math.max(globalThis.devicePixelRatio || 1, 2),
      // Printed, so there is nothing to animate towards, and an animation would
      // only mean the export might capture a half-drawn chart.
      animation: false,
      indexAxis: horizontal ? 'y' : 'x',
      layout: { padding: padding(chart) },
      ...(chart.chartType === 'doughnut' ? { cutout: '58%' } : {}),
      plugins: {
        legend: {
          display: chart.showLegend,
          position:
            chart.legendPosition === 'auto' ? (round ? 'right' : 'bottom') : chart.legendPosition,
          labels: {
            font,
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
            // A bar's key is a square of its colour, a pie's or a line's a dot.
            ...(chart.chartType === 'bar' ? { pointStyle: 'rect' } : {}),
            ...(byLabel ? { generateLabels: () => labelEntries(chart, palette) } : {}),
          },
        },
        tooltip: { enabled: false },
      },
      scales: round ? {} : horizontal ? { x: value, y: category } : { x: category, y: value },
    },
    plugins: [figuresPlugin(chart, look, palette), centrePlugin(chart, look)],
  }
}
